import { UseChatHelpers } from '@ai-sdk/react';
import type { LlmUIMessage } from '@azymmetric/shared/types/llm.types';
import { Conversation, ConversationContent, ConversationEmptyState } from '@components/ai-elements/conversation';
import { Message, MessageContent } from '@components/ai-elements/message';
import { Spinner } from '@components/shared/spinner.component';
import { useScrollToBottom } from '@hooks/use-scroll-to-bottom.hook';
import equal from 'fast-deep-equal';
import { ArrowDownIcon } from 'lucide-react';
import { memo, useEffect } from 'react';
import { Response } from '../../../../ai-elements/response';
import { GenerateSankeyTool } from '../tools/generate-sankey-tool.component';
import { GetAllTechnologiesTool } from '../tools/get-all-technologies-tool.component';
import { GetTechnologyTool } from '../tools/get-technology-tool.component';
import { RenderArmyReadinessReportTool } from '../tools/render-army-readiness-report-tool.component';
import { RenderNavyCostTimeReportTool } from '../tools/render-navy-cost-time-report-tool.component';
import { RenderNavyReadinessReportTool } from '../tools/render-navy-readiness-report-tool.component';
import { RenderSpaceForceReadinessReportTool } from '../tools/render-space-force-readiness-report-tool.component';

export type ChatMessagesProps = {
  chatId: string;
  messages: LlmUIMessage[];
  regenerate: UseChatHelpers<LlmUIMessage>['regenerate'];
  status: UseChatHelpers<LlmUIMessage>['status'];
  setMessages: UseChatHelpers<LlmUIMessage>['setMessages'];
};

function PureChatMessages(props: ChatMessagesProps) {
  const { messages, status } = props;

  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    isAtBottom,
    scrollToBottom,
  } = useScrollToBottom();

  // Determine if we should show the loading spinner
  // Show when status is 'submitted' (before streaming starts) or when streaming but no content yet
  const lastMessage = messages.at(-1);
  const hasAssistantContent = lastMessage?.parts.some((part) => {
    if (part.type === 'text') {
      return Boolean(part.text);
    }

    return part.type.startsWith('tool-');
  });
  const showLoader =
    status === 'submitted' || (status === 'streaming' && lastMessage?.role === 'assistant' && !hasAssistantContent);

  useEffect(() => {
    if (status === 'submitted') {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth',
          });
        }
      });
    }
  }, [status, messagesContainerRef]);

  useEffect(() => {
    if (messages.length === 0) {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTo({ top: 0, behavior: 'instant' });
      }
    }
  }, [messages.length, messagesContainerRef]);

  return (
    <div
      ref={messagesContainerRef}
      className="overscroll-behavior-contain -webkit-overflow-scrolling-touch w-full flex-1 min-h-0 touch-pan-y overflow-y-auto"
      style={{ overflowAnchor: 'none' }}
    >
      <Conversation>
        <ConversationContent>
          {messages.length === 0 && (
            <ConversationEmptyState
              title="Start a Research"
              description="Type a message below to begin"
            />
          )}
          {messages.map((message) => {
            return (
              <Message
                from={message.role}
                key={message.id}
              >
                {message.parts.map((part, i) => {
                  switch (part.type) {
                    case 'text':
                      return (
                        <MessageContent key={`${message.id}-${i}`}>
                          <Response>{part.text}</Response>
                        </MessageContent>
                      );
                    case 'tool-getAllTechnologies':
                      return (
                        <GetAllTechnologiesTool
                          key={`${message.id}-${i}`}
                          messageId={message.id}
                          part={part}
                          partIndex={i}
                        />
                      );
                    case 'tool-getTechnology':
                      return (
                        <GetTechnologyTool
                          key={`${message.id}-${i}`}
                          messageId={message.id}
                          part={part}
                          partIndex={i}
                        />
                      );
                    case 'tool-generateSankey':
                      return (
                        <GenerateSankeyTool
                          key={`${message.id}-${i}`}
                          messageId={message.id}
                          part={part}
                          partIndex={i}
                        />
                      );
                    case 'tool-renderNavyReadinessReport':
                      return (
                        <RenderNavyReadinessReportTool
                          key={`${message.id}-${i}`}
                          messageId={message.id}
                          part={part}
                          partIndex={i}
                        />
                      );
                    case 'tool-renderNavyCostTimeReport':
                      return (
                        <RenderNavyCostTimeReportTool
                          key={`${message.id}-${i}`}
                          messageId={message.id}
                          part={part}
                          partIndex={i}
                        />
                      );
                    case 'tool-renderArmyReadinessReport':
                      return (
                        <RenderArmyReadinessReportTool
                          key={`${message.id}-${i}`}
                          messageId={message.id}
                          part={part}
                          partIndex={i}
                        />
                      );
                    case 'tool-renderSpaceForceReadinessReport':
                      return (
                        <RenderSpaceForceReadinessReportTool
                          key={`${message.id}-${i}`}
                          messageId={message.id}
                          part={part}
                          partIndex={i}
                        />
                      );
                    default:
                      return null;
                  }
                })}
              </Message>
            );
          })}

          {showLoader && (
            <Message from="assistant">
              <MessageContent>
                <div className="flex items-center gap-2 py-2">
                  <Spinner size="sm" />
                </div>
              </MessageContent>
            </Message>
          )}

          <div
            ref={messagesEndRef}
            className="min-h-[24px] min-w-[24px] shrink-0"
          />
        </ConversationContent>
      </Conversation>

      {!isAtBottom && messages.length > 0 && (
        <button
          className="absolute bottom-40 left-1/2 z-10 -translate-x-1/2 rounded-full border bg-background p-2 shadow-lg transition-colors hover:bg-muted"
          onClick={() => scrollToBottom('smooth')}
          type="button"
          aria-label="Scroll to bottom"
        >
          <ArrowDownIcon className="size-4" />
        </button>
      )}
    </div>
  );
}

export const ChatMessages = memo(PureChatMessages, (prevProps, nextProps) => {
  if (prevProps.status !== nextProps.status) return false;
  if (prevProps.messages.length !== nextProps.messages.length) return false;
  if (!equal(prevProps.messages, nextProps.messages)) return false;

  return false;
});
